import React, { useEffect, useRef, useState } from 'react';
import { Pencil, Plus, Trash2 } from 'lucide-react';
import Card, { CardHeader } from '../../common/Card';
import Button from '../../common/Button';
import { useCannedJobs } from '../hooks/useCannedJobs';
import { messageFromError } from './errorMessage';
import CannedJobEditor from './CannedJobEditor';
import { cannedJobSummary } from '../../../lib/cannedJob';

const ARM_MS = 4000;

// Settings -> Canned jobs. Owns its data; SettingsTab stays presentational.
const CannedJobsSection = () => {
  const { cannedJobs, loading, error, reload, create, update, remove } = useCannedJobs();
  const [editing, setEditing] = useState(null); // null | 'new' | job
  const [armedId, setArmedId] = useState(null);
  const [deleteError, setDeleteError] = useState('');
  const timer = useRef(null);

  useEffect(() => () => clearTimeout(timer.current), []);

  const handleDelete = async (job) => {
    setDeleteError('');
    if (armedId !== job.id) {
      setArmedId(job.id);
      clearTimeout(timer.current);
      timer.current = setTimeout(() => setArmedId(null), ARM_MS);
      return;
    }
    clearTimeout(timer.current);
    setArmedId(null);
    try {
      await remove(job.id);
    } catch (err) {
      setDeleteError(messageFromError(err, 'Could not delete this canned job.'));
    }
  };

  const startEditing = (target) => {
    setDeleteError('');
    setEditing(target);
  };

  const handleSave = async (payload) => {
    if (editing && editing !== 'new') await update(editing.id, payload);
    else await create(payload);
    setDeleteError('');
    setEditing(null);
  };

  return (
    <Card padding={false}>
      <CardHeader
        className="p-6 pb-0"
        title="Canned jobs"
        subtitle="Saved jobs you can add to any estimate"
        action={(cannedJobs.length > 0 || error) && (
          <Button variant="secondary" size="sm" icon={Plus} onClick={() => startEditing('new')}>
            New canned job
          </Button>
        )}
      />
      <div className="p-6 space-y-3">
        {(error || deleteError) && (
          <p role="alert" className="text-small text-danger">{error || deleteError}</p>
        )}
        {error && (
          <Button variant="secondary" size="sm" onClick={reload}>Retry</Button>
        )}
        {loading && <p className="text-small text-ink-muted">Loading…</p>}

        {!loading && !error && cannedJobs.length === 0 && (
          <div className="space-y-3">
            <p className="text-body text-ink-muted">
              No canned jobs yet. Save the jobs you do often, then add them to any estimate in one tap.
            </p>
            <Button variant="primary" icon={Plus} onClick={() => startEditing('new')}>
              New canned job
            </Button>
          </div>
        )}

        {cannedJobs.length > 0 && (
          <ul className="divide-y divide-border-default">
            {cannedJobs.map((job) => (
              <li key={job.id} className="flex flex-wrap items-center justify-between gap-3 py-3">
                <div className="min-w-0">
                  <p className="text-body-strong text-ink truncate">{job.name}</p>
                  <p className="text-small text-ink-muted">{cannedJobSummary(job)}</p>
                </div>
                <div className="flex gap-2">
                  <Button
                    variant="secondary"
                    size="sm"
                    icon={Pencil}
                    aria-label={`Edit ${job.name}`}
                    onClick={() => startEditing(job)}
                  >
                    Edit
                  </Button>
                  <Button
                    variant="danger"
                    size="sm"
                    icon={Trash2}
                    aria-label={armedId === job.id ? 'Tap again to delete' : `Delete ${job.name}`}
                    onClick={() => handleDelete(job)}
                  >
                    {armedId === job.id ? 'Tap again to delete' : 'Delete'}
                  </Button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      {editing && (
        <CannedJobEditor
          isOpen
          job={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
          onSave={handleSave}
        />
      )}
    </Card>
  );
};

export default CannedJobsSection;
